import React, { useState } from 'react';
import './BannerLogin.css';
import egurukulappslogo from "./../assets/logo.png";
import LoginForm from './LoginForm';
import FreeTrialModal from './FreeTrialModal';

const scrollToSection = (selector) => {
  const el = document.querySelector(selector);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const features = [
  {
    title: 'Student Management',
    text: 'Complete student lifecycle management from admission to graduation. Track academic progress, attendance, and performance with detailed profiles.',
    icon: (
      <>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
  },
  {
    title: 'Fee Management',
    text: 'Automated fee collection with payment reminders, online payment gateway integration, and detailed financial reports. Reduce manual work by 80%.',
    icon: (
      <>
        <path d="M6 3h12" />
        <path d="M6 8h12" />
        <path d="m6 13 8.5 8" />
        <path d="M6 13h3" />
        <path d="M9 13c6.667 0 6.667-10 0-10" />
      </>
    ),
  },
  {
    title: 'Mobile Apps',
    text: 'Dedicated mobile apps for students, parents, and teachers. Real-time notifications, attendance marking, and communication on the go.',
    icon: (
      <>
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </>
    ),
  },
  {
    title: 'Reports & Analytics',
    text: 'Generate professional reports in seconds. Track performance, analyze trends, and make data-driven decisions to grow your institute.',
    icon: (
      <>
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </>
    ),
  },
  {
    title: 'Secure & Reliable',
    text: 'Bank-level security with automatic backups. Your data is safe with 99.9% uptime guarantee and 24/7 monitoring.',
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  },
  {
    title: 'Easy Setup',
    text: 'Get started in under 30 minutes with our guided setup process. No technical knowledge required - we handle everything for you.',
    icon: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  },
];

const EgurukulApps = () => {
  const [showTrialModal, setShowTrialModal] = useState(false);

  const handleStartFreeTrial = () => setShowTrialModal(true);
  const handleScheduleDemo = () => setShowTrialModal(true);
  const handleWatchDemo = () => console.log('Watch demo clicked');

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src={egurukulappslogo} className="brand-logo-img" alt="EgurukulApps Logo" />
            </div>

            <nav className="nav">
              <button type="button" className="nav-link nav-link-btn" onClick={() => scrollToSection('#features')}>Features</button>
              <button type="button" className="nav-link nav-link-btn" onClick={() => scrollToSection('#pricing')}>Pricing</button>
              <button type="button" className="nav-link nav-link-btn" onClick={handleWatchDemo}>Demo</button>
              <button type="button" className="nav-link nav-link-btn" onClick={() => scrollToSection('#footer')}>Support</button>
              <button type="button" className="nav-link nav-link-btn" onClick={() => scrollToSection('#footer')}>Contact</button>
            </nav>

            <div className="auth-buttons">
              <button
                type="button"
                onClick={() => scrollToSection('.login-form-wrapper')}
                className="login-btn"
              >
                Login
              </button>
              <button type="button" onClick={handleStartFreeTrial} className="trial-btn">
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero and Login */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                The Complete <span className="text-accent">ERP Solution</span> for Your Educational Institute
              </h1>
              <p className="hero-description">
                EgurukulApps helps tuition centers, coaching institutes, and educational organizations manage their operations efficiently. From student admissions to fee collection, from attendance tracking to parent communication  everything is simplified.
              </p>
              <div className="hero-buttons">
                <button type="button" onClick={handleStartFreeTrial} className="btn-primary">
                  Start Free Trial →
                </button>
                <button type="button" onClick={handleWatchDemo} className="btn-secondary">
                  Watch Demo
                </button>
              </div>
              <ul className="hero-points">
                <li>No credit card required</li>
                <li>Setup in under 30 minutes</li>
                <li>Mobile apps included</li>
              </ul>
            </div>

            <div className="login-form-wrapper">
              <LoginForm />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="why-choose" id="features">
        <div className="container">
          <div className="sectionln-header">
            <p className="section-subtitle">Why Choose EgurukulApps?</p>
            <h2 className="section-title">Everything You Need to Manage Your Institute</h2>
            <p className="section-description">
              Comprehensive features designed specifically for educational institutes to streamline operations and boost growth.
            </p>
          </div>
        </div>
      </section>

      <section className="features" id="pricing">
        <div className="container">
          <div className="features-grid">
            {features.map((f) => (
              <div className="feature-card" key={f.title}>
                <div className="feature-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {f.icon}
                  </svg>
                </div>
                <h4>{f.title}</h4>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Institute?</h2>
            <p>
              Join thousands of successful institutes already using EgurukulApps. Start your free trial today - no credit card required.
            </p>
            <div className="cta-buttons">
              <button type="button" onClick={handleStartFreeTrial} className="cta-btn-primary">
                Start Free Trial
              </button>
              <button type="button" onClick={handleScheduleDemo} className="cta-btn-secondary">
                Schedule Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" id="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <h4>Product</h4>
              <ul>
                <li><button type="button" className="footer-link-btn">Features</button></li>
                <li><button type="button" className="footer-link-btn">Pricing</button></li>
                <li><button type="button" className="footer-link-btn">Mobile Apps</button></li>
                <li><button type="button" className="footer-link-btn">Integrations</button></li>
                <li><button type="button" className="footer-link-btn">Security</button></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Solutions</h4>
              <ul>
                <li><button type="button" className="footer-link-btn">Tuition Centers</button></li>
                <li><button type="button" className="footer-link-btn">Coaching Institutes</button></li>
                <li><button type="button" className="footer-link-btn">Schools</button></li>
                <li><button type="button" className="footer-link-btn">Training Centers</button></li>
                <li><button type="button" className="footer-link-btn">Language Centers</button></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Support</h4>
              <ul>
                <li><button type="button" className="footer-link-btn">Help Center</button></li>
                <li><button type="button" className="footer-link-btn">Contact Support</button></li>
                <li><button type="button" className="footer-link-btn">Training</button></li>
                <li><button type="button" className="footer-link-btn">System Status</button></li>
                <li><button type="button" className="footer-link-btn">API Documentation</button></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Company</h4>
              <ul>
                <li><button type="button" className="footer-link-btn">About Us</button></li>
                <li><a href="https://www.egurukulapps.com/contactus" target="_blank" rel="noopener noreferrer">Contact Us</a></li>
                <li><a href="https://www.egurukulapps.com/privacypolicy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
                <li><a href="https://www.egurukulapps.com/cancellation-refunds" target="_blank" rel="noopener noreferrer">Refunds Policy</a></li>
                <li><a href="https://www.egurukulapps.com/termsandconditions" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} EgurukulApps. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {showTrialModal && <FreeTrialModal onClose={() => setShowTrialModal(false)} />}
    </div>
  );
};

export default EgurukulApps;